// React
import { useCallback } from 'react'

// Interfaces
import { IBaseUploaderProps } from './interfaces'

// Components
import { Wrapper, Content } from './components'

// Mobx React
import { observer } from 'mobx-react'

// Custom Hook
import { useAuth } from 'hooks'

// i18n
import { useTranslation } from 'react-i18next'

// Utils
import { fileUtils_getDimensions } from 'utils/common/file.utils'
import { messageUtils_error } from 'utils/common/message.utils'

const BaseUploader = observer(
  ({
    onChange,
    isUsingPreview,
    files,
    fileAcceptFormats,
    isForceAbort,
    isError,
    uploadedFilesLength,
    checkUploadedFilesLength,
    maximumFileUpload,
    maxDimensions,
    ...rest
  }: IBaseUploaderProps) => {
    const { auth_persisted } = useAuth()
    const { t } = useTranslation()

    /**
     * @description Watch any change when user first time inserting image,
     *
     * @param {File} file
     *
     * @return {boolean} boolean
     */
    const fileFilter = useCallback(
      async (file: File | any): Promise<any> => {
        try {
          const dimensions = (await fileUtils_getDimensions(file)) as { width: number; height: number }

          if (dimensions?.width >= maxDimensions?.width! || dimensions?.height >= maxDimensions?.height!) {
            return true
          }

          messageUtils_error(
            `${t(`app.validations.files.minimumWidth`)} ${maxDimensions?.width || 0}px ${t('app.and')} ${t(
              'app.validations.files.minimumHeight',
            )} ${maxDimensions?.height || 0}px`,
          )

          return false
        } catch (err) {
          if (typeof err === 'string') {
            messageUtils_error(t(err))
          }

          return false
        }
      },
      [maxDimensions, t],
    )

    return (
      <Wrapper
        {...rest}
        destination={{
          ...rest?.destination,
          url: `${process.env.REACT_APP_BASE_URL}${rest?.destination?.url}`,
          headers: { Authorization: `Bearer ${auth_persisted?.auth_accessToken || ''}` },
        }}
        fileFilter={fileFilter}
      >
        {/* Content */}
        <Content
          files={files}
          isForceAbort={isForceAbort}
          isUsingPreview={isUsingPreview}
          onChange={onChange}
          isError={isError}
          fileAcceptFormats={fileAcceptFormats}
          uploadedFilesLength={uploadedFilesLength}
          checkUploadedFilesLength={checkUploadedFilesLength}
          maximumFileUpload={maximumFileUpload}
        />

        <p className="mt-4">
          *
          {`${t(`app.validations.files.minimumWidth`)} ${maxDimensions?.width || 0}px ${t('app.and')} ${t(
            'app.validations.files.minimumHeight',
          )} ${maxDimensions?.height || 0}px`}
        </p>
      </Wrapper>
    )
  },
)

export { BaseUploader }
